"use client";

import { Typography, cn } from "@tosize/ui";
import Image from "next/image";
import { Fragment, useMemo, useState } from "react";
import { Link } from "~/components/Link";
import { ShowMoreCard } from "~/components/category/TaxonomyRow/ShowMore";
import type { CategoryPageType, TaxonomyProductsType } from "~/graphql/shared/category";

type TaxonomyVariantProps =
	| {
			variant: "category";
			products: CategoryPageType["children"];
	  }
	| {
			variant: "product";
			products: TaxonomyProductsType["products"]["items"];
	  };

type TaxonomiesProps = TaxonomyVariantProps & {
	messages: {
		showMoreCategory: string;
		showLessCategory: string;
		showMoreProduct: string;
		showLessProduct: string;
	};
};

export function Taxonomies({ products, variant, messages }: TaxonomiesProps) {
	const [open, onOpenChange] = useState(false);
	const name = useMemo(() => {
		if (open) {
			return variant === "category" ? messages.showLessCategory : messages.showLessProduct;
		}

		return variant === "category" ? messages.showMoreCategory : messages.showMoreProduct;
	}, [
		open,
		variant,
		messages.showLessCategory,
		messages.showLessProduct,
		messages.showMoreCategory,
		messages.showMoreProduct,
	]);

	const convert = (value: string | null) => {
		return Number(value?.replace(" mm", ""));
	};

	if (products.length === 0) {
		return null;
	}

	const convertedItems =
		variant === "category"
			? products
					.filter((item) => item.name && item.image)
					.map((item) => ({
						id: item.id,
						name: item.name ?? "",
						url: item.url,
						image: item.image ?? "",
						imageDetail: undefined,
						associatedProducts: undefined,
					}))
			: products
					.filter((item) => item.name && item.imageBase)
					.map((item) => ({
						id: item.id,
						name: item.name ?? "",
						url: item.url,
						image: item.imageBase ?? "",
						imageDetail: item.imageDetail ?? undefined,
						associatedProducts: item.associatedProducts.items
							.filter((item) => !!item.isSalable)
							.sort((a, b) => convert(a.thickness) - convert(b.thickness))
							.map((associatedProduct) => ({
								id: associatedProduct.id,
								url: associatedProduct.url,
								thickness: associatedProduct.thickness,
							})),
					}));

	return (
		<div className="scrollbar-none snap-x overflow-x-auto overflow-y-hidden lg:overflow-visible">
			<div className="flex flex-nowrap lg:grid lg:grid-cols-4 lg:gap-4 xl:grid-cols-5 2xl:grid-cols-6">
				{convertedItems.map((product, index) => (
					<div
						key={product.id}
						className={cn(
							"group flex w-full flex-col overflow-hidden rounded-xl bg-white text-primary-medium leading-tight",
							"m-2 shrink-0 snap-start border border-shades first:ml-0 last:mr-0 lg:m-0 lg:max-w-none lg:border-none lg:shadow",
							"max-w-[calc(310px/1.375)]",
							{
								"[@media((min-width:1024px)_and_(max-width:1279px))]:hidden":
									products.length > 4 && index >= 3 && !open,
								"[@media((min-width:1280px)_and_(max-width:1535px))]:hidden":
									products.length > 5 && index >= 4 && !open,
								"2xl:hidden": products.length > 6 && index >= 5 && !open,
							},
						)}
					>
						<Link
							href={product.url}
							className="flex cursor-pointer flex-col enter:underline focus:outline-none"
						>
							<div className="relative flex aspect-[2/1]">
								<Image
									src={product.image}
									width={620}
									height={310}
									alt={name}
									priority
									className="aspect-[2/1]"
								/>
								{!!product.imageDetail && (
									<span className="invisible absolute top-0 right-0 bottom-0 left-0 opacity-0 group-hover:visible group-hover:opacity-100 group-focus-visible:visible group-focus-visible:opacity-100 motion-safe:transition-opacity">
										<Image
											src={product.imageDetail}
											width={620}
											height={310}
											alt={name}
											className="aspect-[2/1]"
										/>
									</span>
								)}
							</div>
							<div className="mx-4 mt-1 mb-2 flex min-h-[2.5rem] grow items-center justify-center text-center">
								<Typography variant="paragraph-bold" component="span" color="text-primary-medium">
									{product.name}
								</Typography>
							</div>
						</Link>

						{!!product.associatedProducts?.length && (
							<div className="mx-4 mb-4 flex flex-wrap items-center justify-center gap-1">
								{product.associatedProducts.map((associatedProduct, i) => (
									<Fragment key={associatedProduct.id}>
										<Link
											href={associatedProduct.url}
											className="text-shades-dark text-sm leading-none enter:underline focus:outline-none"
										>
											{convert(associatedProduct.thickness)}
										</Link>
										{i === product.associatedProducts.length - 1 ? null : "/"}
									</Fragment>
								))}

								<span className="text-shades-dark text-sm leading-none"> mm</span>
							</div>
						)}
					</div>
				))}

				<ShowMoreCard
					name={name}
					open={open}
					variant={variant}
					onClick={() => onOpenChange(!open)}
					className={cn("hidden", {
						"col-end-[-1] row-start-1": !open,
						"[@media((min-width:1024px)_and_(max-width:1279px))]:flex": products.length > 4,
						"[@media((min-width:1280px)_and_(max-width:1535px))]:flex": products.length > 5,
						"2xl:flex": products.length > 6,
					})}
				/>
			</div>
		</div>
	);
}
